/* Copyright (c) 2019 - 2021. Verimatrix. All Rights Reserved.
   All information in this file is Verimatrix Confidential and Proprietary. */

import React, { useEffect, useState } from "react";
import {
  useApolloClient,
  useLazyQuery,
  useMutation,
} from "@apollo/react-hooks";
import { updateOrganization, removeOrganization } from "./queries";
import DatePicker from "react-datepicker";
import {
  Table,
  Icon,
  Grid,
  Dropdown,
  DropdownProps,
  Dimmer,
  Loader,
  Button,
  Transition,
  Modal,
} from "semantic-ui-react";
import styles from "../MyAccount.module.css";
import cStyles from "../../Components.module.css";
import {
  authCheckCallApi,
  DateFormatLong,
  getTrialExpires,
  getUrlParam,
  getUrlPath,
  resetUrlParams,
  setUrlParam,
} from "../../Authenticator/utils";
import { MessageDialogParams } from "../../../types/MessageDialogParams";
import useForm from "react-hook-form";
import { getCountryName } from "../../Authenticator/countries";
import { ChangeSubscriptionDialog } from "../Payment";
import { Applications, Notes } from ".";
import ConfirmationDialog from "../../ConfirmationDialog";
import {
  InviteUserInput,
  InviteUserMutation,
  UsersQuery,
} from "../../../graphql/user";
import { Organization } from "../../../graphql/organization";
import { Ico } from "../../Icon/Icon";
import { AddUserForm } from "../Contacts";

interface OrgListProps {
  orgData: any;
  ro: boolean;
  onRefresh?: any;
  showSelectedUser: any;
}

type Operation = "Set Trial Expiry" | "Change Plan" | "Remove";

export const planOptions = [
  {
    key: "free",
    value: "free",
    text: "Free Trial",
  },
  {
    key: "protectStarter",
    value: "protectStarter",
    text: "STARTER (deprecated)",
  },
  {
    key: "protectStarterSingleApp",
    value: "protectStarterSingleApp",
    text: "STARTER",
  },
  {
    key: "protectStandard",
    value: "protectStandard",
    text: "STANDARD",
  },
  {
    key: "protectProfessional",
    value: "protectProfessional",
    text: "PRO",
  },
  {
    key: "enhancedSupport",
    value: "enhancedSupport",
    text: "Enterprise - Enhanced Support",
  },
  {
    key: "monitoringSecurityOperationsCenter",
    value: "monitoringSecurityOperationsCenter",
    text: "Monitoring - Security Operations Center",
  },
  {
    key: "monitoringAppInstanceLookup",
    value: "monitoringAppInstanceLookup",
    text: "Monitoring - App Instance Lookup",
  },
  {
    key: "monitoringUltimate",
    value: "monitoringUltimate",
    text: "Monitoring - Ultimate",
  },
  {
    key: "monitoringEndPointProtection",
    value: "monitoringEndPointProtection",
    text: "Monitoring - End Point Protection",
  },
];

const getPlanOptionText = (planId: string) => {
  return planOptions.find((planOption) => planOption.key === planId)?.text;
};

const getPlatformText = (platform: boolean) => {
  return platform ? "yes" : "";
}

const OrganizationsList: React.FC<OrgListProps> = ({
  orgData,
  ro,
  onRefresh,
  showSelectedUser,
}) => {
  const [message, setMessage] = useState<MessageDialogParams | null>(null);
  const [sorting, setSorting] = useState<any>({
    sortBy: "name",
    reverse: false,
  });
  const client = useApolloClient();
  const { setValue } = useForm<FormData>();
  const [loading, setLoading] = useState<boolean>(false);
  const [orgPopupId, setOrgPopupId] = useState<string | null>(null);
  const [showAddUser, setShowAddUser] = useState(false);
  const [changeSubscription, setChangeSubscription] = useState<any>(null);
  const [showAppsOrg, setShowAppsOrg] = useState<any>(null);
  const [trialExpiryDate, setTrialExpiryDate] = useState<Date>(new Date());

  interface PendingOrgAction {
    operation: Operation;
    mutation: any;
    variables: any;
    message: string;
    optionalContent?: (params: any) => void;
    optionalContentProp?: any;
    orgCurrentPlan?: string;
  }

  const [pendingOrgAction, setPendingOrgAction] =
    useState<PendingOrgAction | null>(null);

  type FormData = {
    orgName: string;
    plan: string;
  };
  const emptyInputs = {
    orgName: "",
    plan: "free",
  };
  const [inputs, setInputs] = useState<FormData>(emptyInputs);

  const [getOrgUsers, { loading: orgUsersLoading, data: orgUsersData }] =
    useLazyQuery<any>(UsersQuery, { fetchPolicy: "network-only" });

  const handleDropdownChange = (_: any, data: DropdownProps) => {
    setValue(data.name, data.value);
    setInputs((inputs) => ({ ...inputs, [data.name]: data.value }));
  };

  useEffect(() => {
    const orgParam = getUrlParam("o");
    if (!!orgParam && !!orgData && getUrlPath().endsWith("Organizations")) {
      const org = orgData.find((org: any) => org.name === orgParam);
      if (org) {
        setOrgPopupId(org.id);
      } else {
        resetUrlParams();
      }
    }
  }, [orgData]);

  const orgAction = (operation: Operation, mutation: any, variables: any) => {
    switch (operation) {
      case "Change Plan":
        variables.organization.plan = inputs.plan;
        break;
      case "Set Trial Expiry": {
        variables.organization.trialExpires = trialExpiryDate.getTime();
        break;
      }
      default:
    }

    setLoading(true);
    authCheckCallApi(() => {
      client
        .mutate({
          fetchPolicy: "no-cache",
          mutation: mutation,
          variables: variables,
        })
        .then(() => {
          setMessage({
            title: operation,
            message: `${operation} operation completed successfully.`,
          });
        })
        .catch((e) => {
          setMessage({
            title: operation,
            message: e.message,
          });
        })
        .finally(() => {
          setLoading(false);
          setInputs((inputs) => ({ ...inputs, plan: "free" }));
        });
    });
  };

  const processPendingOrgAction = () => {
    if (!pendingOrgAction) {
      return;
    }
    const operation = pendingOrgAction.operation;
    const mutation = pendingOrgAction.mutation;
    const variables = pendingOrgAction.variables;

    if (operation === "Change Plan") {
      if (
        pendingOrgAction.orgCurrentPlan &&
        pendingOrgAction.orgCurrentPlan === inputs.plan
      ) {
        setMessage({
          title: operation,
          message:
            "This organization is already subscribed to the selected plan.",
        });
        setPendingOrgAction(null);
        setInputs((inputs) => ({ ...inputs, plan: "free" }));
        return;
      }
    }

    setPendingOrgAction(null);
    orgAction(operation, mutation, variables);
  };

  const getStatus = (org: any, fullDate?: boolean) => {
    if (org.plan === "free") {
      const trialExpires = getTrialExpires(org);
      const trialExpiresDate = new Date(trialExpires);
      if (trialExpiresDate.getTime() < new Date().getTime()) {
        return "Trial Expired";
      }
    }

    if (org.planExpires) {
      return `Expires ${new Date(org.planExpires).toLocaleDateString(
        navigator.language,
        fullDate ? DateFormatLong : {}
      )}`;
    }

    return "Active";
  };

  const trialExpiryContent = (expiryDate: Date) => {
    return (
      <div style={{ marginTop: "1rem" }}>
        <DatePicker
          showTimeInput
          selected={expiryDate}
          onChange={(date: Date) => {
            setTrialExpiryDate(date);
          }}
          customInput={
            <Button secondary className={cStyles.buttonSecond}>
              {expiryDate.toLocaleDateString(
                navigator.language,
                DateFormatLong
              )}
            </Button>
          }
        />
      </div>
    );
  };

  const changePlanContent = () => {
    return (
      <Dropdown
        button
        style={{ marginTop: "1rem" }}
        className={styles.myAccountInput}
        name="plan"
        key="plan"
        selection
        defaultValue={"free"}
        options={planOptions}
        onChange={handleDropdownChange}
        icon={<Ico name="down" className={styles.dropdownIcon} />}
      />
    );
  };

  const orgMenu = (org: any) => {
    if (ro) {
      return getStatus(org);
    }

    return (
      <Dropdown
        pointing="top right"
        text={getStatus(org)}
        icon={{ className: "caret down" }}
      >
        <Dropdown.Menu>
          <Dropdown.Item
            text="Applications"
            icon="list alternate"
            onClick={() => {
              setShowAppsOrg(org);
            }}
          />

          { !org.platform && (
            <React.Fragment>
              <Dropdown.Item
                text="Change Plan"
                icon="exchange"
                onClick={() => {
                  setPendingOrgAction({
                    operation: "Change Plan",
                    mutation: updateOrganization,
                    variables: {
                      organization: { id: org.id },
                    },
                    orgCurrentPlan: org.plan,
                    message: "Select the plan to set for this organization",
                    optionalContent: changePlanContent,
                  });
                }}
              />

              { org.plan && org.plan !== "free" && org.plan !== "free" && (
                <Dropdown.Item
                  text={
                    org.planExpires ? "Resume Subscription" : "Cancel Subscription"
                  }
                  icon={org.planExpires ? "undo" : "cancel"}
                  onClick={() => {
                    setChangeSubscription({
                      operation: org.planExpires ? "resume" : "cancel",
                      id: org.id,
                      stripeDisabled: org.planInfo.stripeDisabled,
                    });
                  }}
                />
              )}

              <Dropdown.Item
                text="Set Trial Expiry"
                icon="calendar check outline"
                onClick={() => {
                  setPendingOrgAction({
                    operation: "Set Trial Expiry",
                    mutation: updateOrganization,
                    variables: {
                      organization: { id: org.id },
                    },
                    message: "Set the trial expiration date for this organization:",
                    optionalContent: trialExpiryContent,
                  });
                }}
              />

              <Dropdown.Item
                text="Remove"
                icon="delete"
                onClick={() => {
                  setPendingOrgAction({
                    operation: "Remove",
                    mutation: removeOrganization,
                    variables: { orgId: org.id },
                    message: "The organization will be removed, are you sure?",
                  });
                }}
              />
            </React.Fragment>
          )}
        </Dropdown.Menu>
      </Dropdown>
    );
  };

  const [inviteUser, { loading: inviteUserLoading }] = useMutation(
    InviteUserMutation,
    {
      onError: (e) => {
        const errorMessage = {
          title: "Add user",
          message: "Unable to invite a new user at this time.",
        };
        if (e.message.includes("Already a member")) {
          errorMessage.message =
            "A user with this email address is already a member.";
        } else if (
          e.message.includes("blocked domain") ||
          e.message.includes("Not authorized")
        ) {
          errorMessage.message = "The provided email address is not allowed.";
        }
        setMessage(errorMessage);
      },
      onCompleted: () => {
        setShowAddUser(false);
        getOrgUsers({ variables: { orgId: orgPopupId } });
      },
    }
  );

  const onAddUser = async (inviteUserInput: InviteUserInput) => {
    inviteUserInput.email = inviteUserInput.email.toLocaleLowerCase();
    inviteUserInput.orgId = orgPopupId || "";
    authCheckCallApi(inviteUser, {
      variables: { user: { ...inviteUserInput } },
    });
  };

  const addUserDialog = () => {
    return (
      <Transition transitionOnMount animation="fade" duration="250">
        <Modal
          centered
          closeOnDimmerClick={false}
          size="tiny"
          open
          onClose={() => setShowAddUser(false)}
        >
          {inviteUserLoading && (
            <Dimmer active={true}>
              <Loader size="large" active={true} />
            </Dimmer>
          )}

          <Modal.Content>
            <div
              className={styles.sectionHeaderMargin}
              style={{ display: "flex" }}
            >
              Add User
              <div
                className={cStyles.closeButton}
                onClick={() => setShowAddUser(false)}
              >
                <Ico name="cancel" style={{ color: "#ffffff", margin: 0 }} />
              </div>
            </div>
            <AddUserForm
              onAddUser={onAddUser}
              onCancel={() => setShowAddUser(false)}
              loading={false}
              userRole={"ADMIN"}
              message="Send an invitation to a user to be added to this App Shield account."
            />
          </Modal.Content>
        </Modal>
      </Transition>
    );
  };

  const orgUsersTable = () => {
    return (
      <Table celled>
        <Table.Body>
          <Table.Row key="name">
            <Table.Cell style={{ fontWeight: "700" }}>
              <Icon name="users" style={{ marginRight: "1rem" }} />
              Users
            </Table.Cell>

            <Table.Cell width="8" verticalAlign="top" textAlign="right">
              {orgUsersLoading && (
                <Loader
                  active
                  size="tiny"
                  inline
                  style={{ marginRight: "1rem" }}
                />
              )}
              <Icon
                name="add user"
                size="large"
                style={{ display: "inline" }}
                className={styles.myAccountLink}
                disabled={ro}
                onClick={() => {
                  setShowAddUser(true);
                }}
              />
            </Table.Cell>
          </Table.Row>

          {orgUsersData &&
            orgUsersData.users.map((user: any) => {
              return (
                <Table.Row key={user.name}>
                  <Table.Cell style={{ paddingLeft: "1rem" }}>
                    {user.name}
                  </Table.Cell>
                  <Table.Cell
                    className={styles.myAccountLink}
                    onClick={() => {
                      showSelectedUser(user.email);
                      setOrgPopupId(null);
                    }}
                  >
                    {user.email}
                  </Table.Cell>
                </Table.Row>
              );
            })}
        </Table.Body>
      </Table>
    );
  };

  const orgPopupContent = (orgId: string) => {
    const org = orgData.find((org: Organization) => org.id === orgId);
    if (org) {
      return (
        <div
          style={{ maxHeight: "75vh", overflow: "auto", paddingRight: "5px" }}
        >
          <Table celled>
            <Table.Body>
              <Table.Row key="name">
                <Table.Cell style={{ fontWeight: "700" }}>
                  <Icon name="building" style={{ marginRight: "1rem" }} />
                  {org.name}
                </Table.Cell>
              </Table.Row>

              <Table.Row key="plan">
                <Table.Cell>Plan</Table.Cell>
                <Table.Cell>{getPlanOptionText(org.plan)}</Table.Cell>
              </Table.Row>

              <Table.Row key="platform">
                <Table.Cell>Platform</Table.Cell>
                <Table.Cell>{getPlatformText(org.platform)}</Table.Cell>
              </Table.Row>

              <Table.Row key="created">
                <Table.Cell>Created</Table.Cell>
                <Table.Cell>
                  {org.created &&
                    new Date(org.created).toLocaleDateString(
                      navigator.language,
                      DateFormatLong
                    )}
                </Table.Cell>
              </Table.Row>

              <Table.Row key="updated">
                <Table.Cell>Updated</Table.Cell>
                <Table.Cell>
                  {org.updated &&
                    new Date(org.updated).toLocaleDateString(
                      navigator.language,
                      DateFormatLong
                    )}
                </Table.Cell>
              </Table.Row>

              <Table.Row key="expiry">
                <Table.Cell>Trial Expiry</Table.Cell>
                <Table.Cell>
                  {getTrialExpiry(org, {
                    formatted: true,
                    longDateFormat: true,
                  })}
                </Table.Cell>
              </Table.Row>

              <Table.Row key="stripe">
                <Table.Cell>Stripe</Table.Cell>
                <Table.Cell>{stripeStatus(org)}</Table.Cell>
              </Table.Row>

              <Table.Row key="taxId">
                <Table.Cell>Tax ID</Table.Cell>
                <Table.Cell>
                  {org.stripeTaxId?.value && (
                    <span>
                      {org.stripeTaxId.value}
                      <a
                        href="https://stripe.com/docs/tax/tax-ids#supported-tax-ids"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.myAccountLink}
                        style={{ marginLeft: "1rem" }}
                      >
                        {org.stripeTaxId?.type}
                        <Ico
                          name="open_new"
                          style={{
                            marginLeft: "5px",
                            verticalAlign: "middle",
                            height: "14px",
                          }}
                          className={styles.ico}
                        />
                      </a>
                    </span>
                  )}
                </Table.Cell>
              </Table.Row>

              <Table.Row key="status">
                <Table.Cell>Status</Table.Cell>
                <Table.Cell>{getStatus(org, true)}</Table.Cell>
              </Table.Row>

              <Table.Row key="line1">
                <Table.Cell>Address (Line 1)</Table.Cell>
                <Table.Cell>{org.address?.line1}</Table.Cell>
              </Table.Row>

              <Table.Row key="line2">
                <Table.Cell>Address (Line 2)</Table.Cell>
                <Table.Cell>{org.address?.line2}</Table.Cell>
              </Table.Row>

              <Table.Row key="city">
                <Table.Cell>City</Table.Cell>
                <Table.Cell>{org.address?.city}</Table.Cell>
              </Table.Row>

              <Table.Row key="state">
                <Table.Cell>State</Table.Cell>
                <Table.Cell>{org.address?.state}</Table.Cell>
              </Table.Row>

              <Table.Row key="zip">
                <Table.Cell>Zip</Table.Cell>
                <Table.Cell>{org.address?.zip}</Table.Cell>
              </Table.Row>

              <Table.Row key="country">
                <Table.Cell>Country</Table.Cell>
                <Table.Cell>{getCountryName(org.address?.country)}</Table.Cell>
              </Table.Row>

              <Table.Row key="telephone1">
                <Table.Cell>Telephone 1</Table.Cell>
                <Table.Cell>{org.address?.telephone1}</Table.Cell>
              </Table.Row>

              <Table.Row key="telephone2">
                <Table.Cell>Telephone 2</Table.Cell>
                <Table.Cell>{org.address?.telephone2}</Table.Cell>
              </Table.Row>

              <Table.Row key="id">
                <Table.Cell>ID</Table.Cell>
                <Table.Cell>{org.id}</Table.Cell>
              </Table.Row>
            </Table.Body>
          </Table>

          <Notes org={org} onRefresh={onRefresh} ro={ro} />

          {orgUsersTable()}
        </div>
      );
    }
  };

  const getTrialExpiry = (
    org: any,
    options?: { formatted: boolean; longDateFormat?: boolean }
  ) => {
    const trialDays = org.planInfo?.trialDays;
    if (trialDays) {
      const expires =
        (org.trialStarted || org.created) + trialDays * 24 * 3600 * 1000;
      if (options?.formatted) {
        return new Date(expires).toLocaleDateString(
          navigator.language,
          options?.longDateFormat ? DateFormatLong : {}
        );
      }
      return expires;
    }

    return new Date(getTrialExpires(org)).toLocaleDateString(
      navigator.language,
      options?.longDateFormat ? DateFormatLong : {}
    );
  };

  const stripeStatus = (org: any) => {
    return (
      <>
        {org.planInfo?.stripeDisabled && <div>Disabled</div>}
        {org.stripeInvoicePaymentFailed && <div>Payment Failed</div>}
      </>
    );
  };

  const orgRow = (org: any, idx: number) => {
    return (
      <Table.Row
        key={idx}
        active={orgPopupId === org.id}
        style={{ cursor: "pointer" }}
        onClick={() => {
          if (!window.getSelection()?.toString().length) {
            getOrgUsers({ variables: { orgId: org.id } });
            setOrgPopupId(org.id);
            if (getUrlPath().endsWith("Organizations")) {
              setUrlParam("o", org.name);
            }
          }
        }}
      >
        <Table.Cell>{org.name}</Table.Cell>
        <Table.Cell>
          {org.address && getCountryName(org.address.country)}
        </Table.Cell>
        <Table.Cell>{getPlanOptionText(org.plan)}</Table.Cell>
        <Table.Cell>{getPlatformText(org.platform)}</Table.Cell>
        <Table.Cell>{getTrialExpiry(org, { formatted: true })}</Table.Cell>
        <Table.Cell>{stripeStatus(org)}</Table.Cell>
        <Table.Cell>
          {org.created &&
            new Date(org.created).toLocaleDateString(navigator.language)}
        </Table.Cell>
        <Table.Cell>
          {org.updated &&
            new Date(org.updated).toLocaleDateString(navigator.language)}
        </Table.Cell>
        <Table.Cell>{orgMenu(org)}</Table.Cell>
      </Table.Row>
    );
  };

  const sortOrgs = (orgA: any, orgB: any) => {
    let sortValueA, sortValueB;

    switch (sorting.sortBy) {
      case "country":
        sortValueA = getCountryName(orgA.address.country);
        sortValueB = getCountryName(orgB.address.country);
        break;

      case "plan":
        sortValueA = orgA.plan;
        sortValueB = orgB.plan;
        break;

      case "trialExpiry":
        sortValueA = getTrialExpiry(orgA);
        sortValueB = getTrialExpiry(orgB);
        break;

      case "created":
        sortValueA = orgA.created;
        sortValueB = orgB.created;
        break;

      case "updated":
        sortValueA = orgA.updated;
        sortValueB = orgB.updated;
        break;

      case "status":
        sortValueA = getStatus(orgA);
        sortValueB = getStatus(orgB);
        break;

      case "stripe":
        sortValueA = orgA.planInfo && orgA.planInfo.stripeDisabled;
        sortValueB = orgB.planInfo && orgB.planInfo.stripeDisabled;
        break;
      case "platform":
        sortValueA = getPlatformText(orgA.platform);
        sortValueB = getPlatformText(orgB.platform);
        break;
      default:
        sortValueA = orgA.name ? orgA.name.toLocaleLowerCase() : null;
        sortValueB = orgB.name ? orgB.name.toLocaleLowerCase() : null;
    }

    if (!sortValueA) {
      sortValueA = "";
    }
    if (!sortValueB) {
      sortValueB = "";
    }

    if (sortValueA < sortValueB) {
      return sorting.reverse ? 1 : -1;
    } else if (sortValueA > sortValueB) {
      return sorting.reverse ? -1 : 1;
    }
    return 0;
  };

  const setOrgSorting = (sortBy: string) => {
    setSorting({
      sortBy: sortBy,
      reverse: sortBy === sorting.sortBy ? !sorting.reverse : false,
    });
  };

  const getSortIndicator = (columnName: string) => {
    if (sorting.sortBy === columnName) {
      return (
        <Icon
          name={sorting.reverse ? "triangle down" : "triangle up"}
          style={{ marginLeft: "3px" }}
        />
      );
    }
    return null;
  };

  if (orgData && orgData.length > 0) {
    const sortedOrgs = orgData.sort((orgA: any, orgB: any) =>
      sortOrgs(orgA, orgB)
    );

    return (
      <React.Fragment>
        <Dimmer active={loading}>
          <Loader active={loading} size="large">
            Please wait...
          </Loader>
        </Dimmer>
        <Table
          selectable
          sortable
          celled
          compact="very"
          className={styles.usersTable}
        >
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell
                onClick={() => {
                  setOrgSorting("name");
                }}
              >
                Company Name
                {getSortIndicator("name")}
              </Table.HeaderCell>
              <Table.HeaderCell
                onClick={() => {
                  setOrgSorting("country");
                }}
              >
                Country
                {getSortIndicator("country")}
              </Table.HeaderCell>
              <Table.HeaderCell
                onClick={() => {
                  setOrgSorting("plan");
                }}
              >
                Plan
                {getSortIndicator("plan")}
              </Table.HeaderCell>
              <Table.HeaderCell
                onClick={() => {
                  setOrgSorting("platform");
                }}
              >
                Platform
                {getSortIndicator("platform")}
              </Table.HeaderCell>
              <Table.HeaderCell
                onClick={() => {
                  setOrgSorting("trialExpiry");
                }}
              >
                Trial Expiry
                {getSortIndicator("trialExpiry")}
              </Table.HeaderCell>
              <Table.HeaderCell
                onClick={() => {
                  setOrgSorting("stripe");
                }}
              >
                Stripe
                {getSortIndicator("stripe")}
              </Table.HeaderCell>

              <Table.HeaderCell
                onClick={() => {
                  setOrgSorting("created");
                }}
              >
                Created
                {getSortIndicator("created")}
              </Table.HeaderCell>
              <Table.HeaderCell
                onClick={() => {
                  setOrgSorting("updated");
                }}
              >
                Updated
                {getSortIndicator("updated")}
              </Table.HeaderCell>
              <Table.HeaderCell
                onClick={() => {
                  setOrgSorting("status");
                }}
              >
                Status
                {getSortIndicator("status")}
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {sortedOrgs.map((org: any, idx: number) => orgRow(org, idx))}
          </Table.Body>
        </Table>

        {message && (
          <ConfirmationDialog
            title={message.title}
            message={message.message}
            onOk={() => {
              setMessage(null);
              onRefresh && onRefresh();
            }}
          />
        )}
        {pendingOrgAction && (
          <ConfirmationDialog
            title={pendingOrgAction.operation}
            message={pendingOrgAction.message}
            onOk={() => {
              processPendingOrgAction();
            }}
            cancelTitle="Cancel"
            onCancel={() => {
              setPendingOrgAction(null);
            }}
            optionalContent={pendingOrgAction.optionalContent}
            optionalContentProp={trialExpiryDate}
          />
        )}
        {changeSubscription && (
          <ChangeSubscriptionDialog
            isCancel={changeSubscription.operation === "cancel"}
            orgId={changeSubscription.id}
            stripeDisabled={changeSubscription.stripeDisabled}
            onCloseDialog={() => {
              setChangeSubscription(null);
              onRefresh && onRefresh();
            }}
            manage
          />
        )}
        {showAppsOrg && (
          <Applications org={showAppsOrg} onCloseDialog={setShowAppsOrg} />
        )}

        {orgPopupId && (
          <ConfirmationDialog
            title={"Organization Info"}
            message={""}
            onOk={() => {
              setOrgPopupId(null);
              resetUrlParams();
            }}
            optionalContent={() => orgPopupContent(orgPopupId)}
            closeOnDimmerClick={true}
          />
        )}

        {showAddUser && addUserDialog()}
      </React.Fragment>
    );
  }

  return (
    <Grid>
      <Grid.Column textAlign="center">
        <div className={styles.myAccountNoUsersLabel}>
          {(orgData && "No data.") || ""}
        </div>
      </Grid.Column>
    </Grid>
  );
};

export default OrganizationsList;
